import { isAuthRedirectInFlight } from './shared/spa-auth.js';

const ACTIVITY_STORAGE_KEY = 'kiosk_session_last_activity_ms';
const LOADER_IDLE_HOLD_MS = 60 * 1000;

export function isSessionIdleOverlayVisible() {
    const root = document.getElementById('session-idle-overlay');
    return root !== null && !root.hidden;
}

export function initSessionIdleOverlay() {
    const root = document.getElementById('session-idle-overlay');
    if (!root) {
        return;
    }

    const raw = root.getAttribute('data-config');
    if (!raw) {
        return;
    }

    let cfg = {};
    try {
        cfg = JSON.parse(raw);
    } catch (_e) {
        return;
    }

    const quietMs = Number(cfg.quietMs);
    const countdownSeconds = Number(cfg.countdownSeconds);
    const keepaliveMaxIntervalMs = Number(cfg.keepaliveMaxIntervalMs);
    if (
        !Number.isFinite(quietMs) ||
        quietMs <= 0 ||
        !Number.isFinite(countdownSeconds) ||
        countdownSeconds <= 0 ||
        !Number.isFinite(keepaliveMaxIntervalMs) ||
        keepaliveMaxIntervalMs <= 0
    ) {
        return;
    }

    if (typeof cfg.logoutUrl !== 'string' || cfg.logoutUrl.length === 0) {
        return;
    }

    if (root.dataset.sessionIdleInit === '1') {
        return;
    }
    root.dataset.sessionIdleInit = '1';

    const logoutUrl = cfg.logoutUrl;
    const keepaliveUrl = cfg.keepaliveUrl;
    const totalIdleMs = quietMs + countdownSeconds * 1000;
    const keepaliveIntervalMs = Math.min(keepaliveMaxIntervalMs, quietMs);
    let lastKeepaliveAt = Date.now();

    function sendKeepalive() {
        if (typeof keepaliveUrl !== 'string' || keepaliveUrl.length === 0) {
            return;
        }
        if (isAuthRedirectInFlight()) {
            return;
        }
        lastKeepaliveAt = Date.now();
        window.fetch(keepaliveUrl, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }).then(function (response) {
            if (response.status === 401) {
                forceLogout();
            }
        }).catch(function (error) { console.error(error); });
    }

    const countdownEl = root.querySelector('.session-idle-overlay__countdown');
    const dismissAnimationMs = 180;
    const storageWriteMinMs = 1000;
    let countdownTimer = null;
    let hideTimer = null;
    let countdownEndsAt = 0;
    let overlayVisible = false;
    let overlayHiding = false;
    let lastStorageWriteAt = 0;
    let loaderHoldStartedAt = 0;

    function elementIsShown(el) {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
    }

    function loadingSpinnerShowing() {
        const pageSpinners = document.querySelectorAll('#overlay');
        for (let i = 0; i < pageSpinners.length; i++) {
            if (elementIsShown(pageSpinners[i])) {
                return true;
            }
        }
        return document.querySelector('.is-ajax-loading') !== null;
    }

    function holdIdleWhileLoading() {
        if (!loadingSpinnerShowing()) {
            loaderHoldStartedAt = 0;
            return false;
        }
        const now = Date.now();
        if (loaderHoldStartedAt === 0) {
            loaderHoldStartedAt = now;
        }
        if (now - loaderHoldStartedAt >= LOADER_IDLE_HOLD_MS) {
            return false;
        }
        if (overlayVisible) {
            hideOverlay();
        } else {
            touchSharedActivity();
        }
        return true;
    }

    function touchSharedActivity(event) {
        if (overlayVisible) {
            return;
        }
        const now = Date.now();
        window.last_activity = now;
        const isScroll = event && event.type === 'scroll';
        if (isScroll && now - lastStorageWriteAt < storageWriteMinMs) {
            return;
        }
        lastStorageWriteAt = now;
        localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
    }

    function stopCountdown() {
        if (countdownTimer) {
            window.clearInterval(countdownTimer);
            countdownTimer = null;
        }
        countdownEndsAt = 0;
    }

    function forceLogout() {
        stopCountdown();
        window.location.replace(logoutUrl);
    }

    function idleFor() {
        return Date.now() - window.last_activity;
    }

    function tickCountdown() {
        if (!overlayVisible || countdownEndsAt <= 0) {
            return;
        }
        const secsLeft = Math.max(0, Math.ceil((countdownEndsAt - Date.now()) / 1000));
        if (countdownEl) {
            countdownEl.textContent = String(secsLeft);
        }
        if (secsLeft <= 0) {
            forceLogout();
        }
    }

    function showOverlay() {
        if (overlayVisible || overlayHiding || isAuthRedirectInFlight()) {
            return;
        }
        overlayVisible = true;
        root.removeAttribute('hidden');
        window.requestAnimationFrame(function () {
            root.classList.add('session-idle-overlay--visible');
        });
        stopCountdown();
        countdownEndsAt = Date.now() + countdownSeconds * 1000;
        tickCountdown();
        countdownTimer = window.setInterval(tickCountdown, 1000);
    }

    function hideOverlay() {
        if (!overlayVisible || overlayHiding) {
            return;
        }
        overlayVisible = false;
        overlayHiding = true;
        root.classList.remove('session-idle-overlay--visible');
        stopCountdown();
        touchSharedActivity();
        if (hideTimer) {
            window.clearTimeout(hideTimer);
        }
        hideTimer = window.setTimeout(function () {
            root.setAttribute('hidden', '');
            overlayHiding = false;
            hideTimer = null;
        }, dismissAnimationMs);
        sendKeepalive();
    }

    function poll() {
        if (holdIdleWhileLoading()) {
            return;
        }
        if (document.visibilityState !== 'visible') {
            if (idleFor() >= totalIdleMs) {
                forceLogout();
            }
            return;
        }
        if (overlayVisible) {
            return;
        }
        if (idleFor() >= quietMs) {
            showOverlay();
            return;
        }
        if (Date.now() - lastKeepaliveAt >= keepaliveIntervalMs && idleFor() < keepaliveIntervalMs) {
            sendKeepalive();
        }
    }

    root.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        hideOverlay();
    });

    window.addEventListener('storage', function (event) {
        if (event.key !== ACTIVITY_STORAGE_KEY) {
            return;
        }
        const incoming = Number(event.newValue);
        if (!Number.isFinite(incoming)) {
            return;
        }
        if (incoming > Number(window.last_activity || 0)) {
            window.last_activity = incoming;
        }
    });

    const opts = { capture: true, passive: true };
    ['pointerdown', 'keydown', 'scroll'].forEach(function (evt) {
        document.addEventListener(evt, touchSharedActivity, opts);
    });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') {
            return;
        }
        if (holdIdleWhileLoading()) {
            return;
        }
        if (idleFor() >= totalIdleMs) {
            forceLogout();
            return;
        }
        poll();
        tickCountdown();
    });

    touchSharedActivity();
    window.setInterval(poll, 500);
}
